import { useState, type FC } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

type Props = {
  open: boolean;
  count: number;
  /** First name when every entry is one person's, for the title. */
  to?: string | null;
  onOpenChange: (open: boolean) => void;
  onReturn: (note: string) => Promise<unknown>;
};

export const ReturnDialog: FC<Props> = ({
  open,
  count,
  to,
  onOpenChange,
  onReturn
}) => {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      await onReturn(note.trim());
      setNote("");
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="return-dialog">
        <DialogHeader>
          <DialogTitle>
            Return {count} {count === 1 ? "entry" : "entries"}
            {to ? ` to ${to}` : ""}
          </DialogTitle>
          <DialogDescription>
            They&apos;ll see your note on their timesheet, fix the entry and
            release it again.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          aria-label="What to fix"
          placeholder="What should change? For example: split the client call from the RFE drafting."
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
          rows={4}
          autoFocus
        />
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!note.trim() || busy}
            onClick={() => void submit()}
          >
            Return with note
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
